import React from 'react';
import _ from 'lodash';
import ArrayUtil from '../../util/ArrayUtil';
import MetadataSpec from '../../queries/metadata/MetadataSpec';
import MetadataUtil from '../../util/MetadataUtil';
import StringUtil from '../../util/StringUtil';
import HashMap from '../../protocol/HashMap';
import HashSet from '../../protocol/HashSet';
import MetadataPanel from '../../components/metadata/MetadataPanel';
import { EditMetadataMsg, FuidRrnInfo, RepoItem } from '../../autogenerated/ZoomBridge';
import EditMetadata from '../../queries/metadata/EditMetadata';
import Modal from '../Modal2';
import Button from '../../common/Button';
import ParamsUtil from '../../util/ParamsUtil';
import './EditAssetsModal.css';
import t from '../../i18n/I18n';
import ConnectionUtil from '../../util/session/ConnectionUtil';
import { ThemeSetup } from '../../themes/ThemeSetup';
import System from '../../basic/System';
import KeyboardShortcuts from '../../keyboard-shortcuts/KeyboardShortcuts';
import KeydownConfig from '../../keyboard-shortcuts/KeydownConfig';
import { KeyCode } from '../../util/Constants';

type Props = {
  assets: RepoItem[],
  onClose: Function
};

type State = {
  groupCode2PropIdMetadataValueMap: ?HashMap,
  filterEmptyValues: boolean
};

export default class EditAssetsModal extends React.Component<Props, State> {
  selectedAssets: RepoItem[];

  constructor(props: Props) {
    super(props);

    this.state = {
      groupCode2PropIdMetadataValueMap: null,
      filterEmptyValues: false
    };

    this.selectedAssets = new Set();
    this.props.assets.forEach((asset) => this.selectedAssets.add(asset));
  }

  componentDidMount() {
    if (ArrayUtil.isEmpty(window.gs.editableMetadataProps)) {
      this.fetchEditbleProps();
    } else {
      this.gatherMetadataKeyValueMap();
    }
    this.addKeyListeners();
  }
  componentWillUnmount() {
    this.removeKeyListener();
  }
  onEnterPress = () => {
    this.issueEditMetadataQuery();
  };
  addKeyListeners = () => {
    KeyboardShortcuts.add(new KeydownConfig('EDIT_METADATA', this.onEnterPress, [KeyCode.ENTER]));
  };
  removeKeyListener = () => {
    KeyboardShortcuts.remove('EDIT_METADATA');
  };
  toggleCheckBox = (item) => {
    if (this.selectedAssets.has(item)) {
      this.selectedAssets.delete(item);
    } else {
      this.selectedAssets.add(item);
    }
    this.gatherMetadataKeyValueMap();
  };

  fetchEditbleProps = () => {
    new MetadataSpec(true, true).run().then(
      (resp) => {
        this.gatherMetadataKeyValueMap();
      },
      (error) => {}
    );
  };

  onChangeHandler(fullPropName, propValue) {
    if (!this.dirtyPropName2ValueMap) {
      this.dirtyPropName2ValueMap = new HashMap();
    }
    this.dirtyPropName2ValueMap.put(fullPropName, propValue);
  }

  gatherMetadataKeyValueMap = () => {
    const groupCode2PropIdMetadataValueMap = MetadataUtil.collateEditablePropsInAssets(this.selectedAssets);
    this.setState({ groupCode2PropIdMetadataValueMap: groupCode2PropIdMetadataValueMap });
  };

  renderFilter = () => {
    const { filterEmptyValues } = this.state;
    return (
      <Button
        icon={filterEmptyValues ? ThemeSetup.getImageURL('filter_red.svg') : 'images/filter_gray.svg'}
        title={t('EDIT_ASSET_MODAL.HIDE_EMPTY_VALUES')}
        onClick={() => this.setState({ filterEmptyValues: !filterEmptyValues })}
      />
    );
  };

  render() {
    const { onClose } = this.props;

    const { filterEmptyValues } = this.state;

    const modalProps = {
      name: 'EditAssetsModal',
      onClose,
      header: (
        <header>
          <span>{t('EDIT_ASSET_MODAL.EDIT_METADATA')}</span>
          {this.renderFilter()}
          <Button type="CAPS" onClick={this.sendQueryForExportMetadata}>
            {t('EDIT_ASSET_MODAL.EXPORT')}
          </Button>
        </header>
      ),
      buttons: [
        { onClick: onClose, label: t('CANCEL'), type: 'CANCEL' },
        { onClick: this.issueEditMetadataQuery, label: t('SAVE'), type: 'PRIMARY' }
      ]
    };

    return (
      /* I have delibrately disabled the closeOnOutsideClick, as sub components lie drop down, which is a portal making the parent to dismiss and as it is trated as outside click.
      We should use portal in a portal... - Om*/
      <Modal {...modalProps} closeOnEsc={true} closeOnOutsideClick={false}>
        <div style={styles.container}>
          <MetadataPanel
            groupCode2PropIdMetadataValueMap={this.state.groupCode2PropIdMetadataValueMap}
            onChangeHandler={(fullPropName, propValue) => this.onChangeHandler(fullPropName, propValue)}
            applyPaddingTop={false}
            panelOpen={false}
            filterEmptyValues={filterEmptyValues}
            onKeyDownEnter={this.issueEditMetadataQuery}
          />
        </div>
      </Modal>
    );
  }

  issueEditMetadataQuery = () => {
    if (this.dirtyPropName2ValueMap !== undefined) {
      let __deletedPropIds = undefined;
      let __newlyAddedPropIds2Values = undefined;
      const __iterator = this.dirtyPropName2ValueMap.iterator();
      let __appendValues = false;

      while (__iterator.hasNext()) {
        const mapEntry = __iterator.next();
        const __dirtyFullPropName = mapEntry.getKey();
        const __dirtPropValue = _.trim(mapEntry.getValue());
        const { groupCode, propId } = MetadataUtil.splitGroupCodeAndPropName(__dirtyFullPropName);
        const __origPropValue = this.state.groupCode2PropIdMetadataValueMap[groupCode].get(propId);

        const __isDirtyValueDeleted = StringUtil.isEmpty(__dirtPropValue);

        if (__dirtPropValue !== __origPropValue && (__origPropValue !== undefined || !__isDirtyValueDeleted)) {
          if (__isDirtyValueDeleted) {
            if (__deletedPropIds === undefined) __deletedPropIds = new HashSet();
            __deletedPropIds.add(__dirtyFullPropName);
          } else {
            if (__newlyAddedPropIds2Values === undefined) __newlyAddedPropIds2Values = new HashMap();
            __newlyAddedPropIds2Values.put(__dirtyFullPropName, __dirtPropValue);
            __appendValues = __appendValues || _.indexOf(__dirtPropValue, MetadataUtil.MULTIPLE_VALUES) > -1;
          }
        }
      }

      if (__deletedPropIds !== undefined || __newlyAddedPropIds2Values !== undefined) {
        const msg = new EditMetadataMsg();
        msg.setDelData(__deletedPropIds);
        msg.setNewData(__newlyAddedPropIds2Values);
        const __assetInfo = [];
        for (let asset of this.selectedAssets) {
          const __fuidBidRrnInfo = new FuidRrnInfo();
          __fuidBidRrnInfo._fuid = asset.getFuid();
          __fuidBidRrnInfo._bid = asset.getBid();
          __fuidBidRrnInfo._rrn = asset.getRrn();

          __assetInfo.push(__fuidBidRrnInfo);
        }
        msg.setAssetsInfo(__assetInfo);
        msg.setAppend(__appendValues);
        msg.setMultipleValues(__appendValues);
        this.sendEditMetadataQuery(msg);
      }
    }

    this.props.onClose(true);
  };

  sendEditMetadataQuery = (msg) => {
    new EditMetadata(msg, this.selectedAssets, 'editModal').run(false, true).then(
      (resp) => {},
      (error) => {}
    );
  };

  sendQueryForExportMetadata = () => {
    var fileName = 'multiple-asset-metadata';
    var assetName;
    var fuidList = [];
    var rrnList = [];
    var bid = 1;
    for (let asset of this.selectedAssets) {
      fuidList.push(asset.getFuid());
      rrnList.push(asset.getRrn());
    }
    var url = ConnectionUtil.getPreviewServerUrl() + '/review/api/asset/export/metadata/';
    const __url = ParamsUtil.fuidsRrnsBidUrl(url, fuidList, rrnList, bid);
    if (System.isDesktop()) window.open(__url, '_blank', 'left=100, top=100, width=200, height=200');
    else window.open(__url, '_blank');
  };
}

const styles = {
  titleBlock: {
    overflow: 'hidden',
    background: '#FAFAFA'
  },
  title: {
    float: 'left',
    paddingBottom: '13px',
    height: '37px',
    color: '#7A7A7A',
    fontFamily: 'Montserrat',
    fontSize: '16px'
  },
  exportBtn: {
    float: 'right',
    color: '#EB6A5C',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '1px',
    lineHeight: '24px',
    cursor: 'pointer'
  },
  buttonBlock: {
    borderTop: '1px solid #E7E7E7',
    marginRight: '-15px',
    marginLeft: '-15px',
    padding: '20px',
    fontSize: '14px',
    fontWeight: '600',
    height: '55px'
  },
  wrapper: {
    padding: '15px 15px 0 15px',
    margin: 'auto',
    background: '#FAFAFA',
    height: '70%',
    minWidth: '480px'
  },
  container: {
    padding: '0 1em 1em 0',
    margin: 'auto',
    background: 'var(--primary-background)',
    height: 'calc(100% - 91px)'
    // overflowY: 'auto'
  },
  editAssetsModal: {
    position: 'absolute',
    left: '0px',
    top: '0px',
    bottom: '0px',
    right: '0px',
    background: 'rgba(0, 0, 0, 0.2)',
    zIndex: '1',
    display: 'flex'
  },
  asset: {
    width: '100%',
    background: 'white',
    padding: '5px',
    display: 'flex',
    boxShadow: '0 1px 4px 0 rgba(0,0,0,0.04)',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '15px'
  }
};
